import type {
  PictureCharacterMatchPayload,
  SoundBubblePayload,
  StorySequencePayload
} from "@/content/chinese";
import { localOggAudioReferenceSchema } from "@/domain/schemas";
import type { ActiveHintStage } from "@/game/feedback";
import type { SequenceHint } from "@/game/templates/sequence";

import { defineCurriculumVoiceCue, type CurriculumVoiceCue } from "./cue";

function getAudioFileStem(audioPath: string): string {
  const path = localOggAudioReferenceSchema.parse(audioPath);
  const fileName = path.split("/").at(-1);

  if (fileName === undefined) {
    throw new Error("Audio path has no file name: " + path);
  }

  return fileName.slice(0, -".ogg".length);
}

export function getSoundBubbleFeedbackCue(
  payload: SoundBubblePayload,
  stage: ActiveHintStage,
  kind: "hint" | "retry"
): CurriculumVoiceCue {
  if (kind === "hint" && stage === "replay-demonstration") {
    return defineCurriculumVoiceCue(
      "/audio/chinese/feedback/sound-bubbles-replay-demonstration.ogg",
      "点点把声音再放一遍。听完，看看亮起来的选项。"
    );
  }

  return defineCurriculumVoiceCue(
    "/audio/chinese/feedback/sound-bubbles-" +
      getAudioFileStem(payload.soundAudio) +
      "-hint.ogg",
    payload.hint
  );
}

export function getPictureCharacterFeedbackCue(
  levelId: string,
  payload: PictureCharacterMatchPayload,
  stage: ActiveHintStage,
  kind: "hint" | "retry"
): CurriculumVoiceCue {
  if (kind === "hint" && stage === "replay-demonstration") {
    return defineCurriculumVoiceCue(
      "/audio/chinese/feedback/picture-character-replay-demonstration.ogg",
      "一幅图和一个汉字亮起来了。看看它们是不是一对。"
    );
  }

  return defineCurriculumVoiceCue(
    "/audio/chinese/feedback/" + levelId + "-hint.ogg",
    payload.hint
  );
}

function getStoryItemLabel(
  payload: StorySequencePayload,
  itemId: string
): string {
  return payload.items.find(({ id }) => id === itemId)?.label ?? "";
}

export function getStorySequenceFeedbackCue(
  payload: StorySequencePayload,
  kind: "hint" | "retry",
  hint: SequenceHint
): CurriculumVoiceCue {
  const pathPrefix =
    "/audio/chinese/feedback/story-sequence-" +
    getAudioFileStem(payload.narrationAudio);

  if (kind === "retry") {
    return defineCurriculumVoiceCue(
      pathPrefix + "-retry.ogg",
      "卡片还有一点没排好。" + payload.hint
    );
  }

  switch (hint.stage) {
    case "gentle-hint":
      return defineCurriculumVoiceCue(
        pathPrefix + "-hint.ogg",
        payload.hint
      );
    case "reduced-choices":
      return defineCurriculumVoiceCue(
        pathPrefix + "-reduced-choices.ogg",
        "两张卡片亮起来了。" + payload.hint
      );
    case "replay-demonstration": {
      const labels = hint.order
        .map((itemId) => getStoryItemLabel(payload, itemId))
        .filter((label) => label.length > 0);
      return defineCurriculumVoiceCue(
        pathPrefix + "-replay-demonstration.ogg",
        "点点这样排：" + labels.join("，接着") + "。"
      );
    }
  }
}
